import { apiFetch } from '@/lib/api';

export interface CreditBalance {
  balanceCredits: number;
  nearestExpiry: string | null;
  /** Whether the user can afford at least one generation at the cheapest model */
  canGenerate: boolean;
  /** Subscription status from org_subscriptions */
  subscriptionStatus?: string | null;
  /** Whether the subscription is set to cancel at period end */
  cancelAtPeriodEnd?: boolean;
  /** When the current period/trial ends */
  currentPeriodEnd?: string | null;
}

export interface CreditGrant {
  id: string;
  organization_id: string;
  amount_credits: number;
  remaining_credits: number;
  description: string | null;
  granted_by: string | null;
  expires_at: string;
  created_at: string;
}

export interface CreditTransaction {
  id: string;
  organization_id: string;
  grant_id: string | null;
  llm_usage_log_id: string | null;
  type: 'grant' | 'debit' | 'refund' | 'expiry';
  amount_credits: number;
  description: string | null;
  created_at: string;
}

export interface CreditSummary {
  balance: CreditBalance;
  activeGrants: CreditGrant[];
  recentTransactions: CreditTransaction[];
}

export interface TransactionListResult {
  items: CreditTransaction[];
  total: number;
}

export class CreditService {
  async getBalance(): Promise<CreditBalance> {
    const res = await apiFetch('/api/credits/balance');
    const json = await res.json();
    if (!json.success) throw new Error(json.error?.message || 'Failed to fetch credit balance');
    return json.data;
  }

  async getSummary(): Promise<CreditSummary> {
    const res = await apiFetch('/api/credits/summary');
    const json = await res.json();
    if (!json.success) throw new Error(json.error?.message || 'Failed to fetch credit summary');
    return json.data;
  }

  async listTransactions(opts: { limit?: number; offset?: number; type?: string } = {}): Promise<TransactionListResult> {
    const params = new URLSearchParams();
    if (opts.limit) params.set('limit', String(opts.limit));
    if (opts.offset) params.set('offset', String(opts.offset));
    if (opts.type) params.set('type', opts.type);
    const res = await apiFetch(`/api/credits/transactions?${params.toString()}`);
    const json = await res.json();
    if (!json.success) throw new Error(json.error?.message || 'Failed to fetch transactions');
    return json.data;
  }
}
